const pulseRadius = radius => ({
  borderTopLeftRadius: radius,
  borderTopRightRadius: radius,
  borderBottomLeftRadius: radius,
  borderBottomRightRadius: radius,
})

const noBorder = {
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
}

const pulseGroupState = ($theme, error, missing) => {
  if (error)
    return { backgroundColor: $theme.colors.dangerPulseBg, boxShadow: `inset 0 0 0 1px ${$theme.colors.dangerPulse}` }
  if (missing) {
    return { backgroundColor: $theme.colors.missPulseBg, boxShadow: `inset 0 0 0 1px ${$theme.colors.missPulseBorder}` }
  }
  return { backgroundColor: $theme.colors.bgPulseCanvas, boxShadow: 'none' }
}

export const SDButtonGroupPulseOverrides = (direction, error, missing) => ({
  Root: {
    style: ({ $theme }) => ({
      flexDirection: direction ?? 'row',
      flexWrap: 'wrap',
      gap: '4px',
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingLeft: '4px',
      paddingRight: '4px',
      marginLeft: 0,
      ...noBorder,
      ...pulseRadius('8px'),
      ...pulseGroupState($theme, error, missing),
    }),
  },
})

export const pulseSelectedSurface = ($theme, $disabled) =>
  $disabled
    ? { backgroundColor: $theme.colors.white, boxShadow: `inset 0 0 0 1px ${$theme.colors.borderPulseHover}` }
    : { backgroundColor: $theme.colors.primaryPulse, boxShadow: 'none' }

const transparentSurface = { backgroundColor: 'transparent', boxShadow: 'none' }

const pulseSegmentColors = ($theme, $isSelected, $disabled, withPill) => {
  const selectedColor = $disabled ? $theme.colors.textPulseMuted : $theme.colors.white
  const idleColor = $disabled ? $theme.colors.textPulseFaint : $theme.colors.textPulseLabel
  const surface = $isSelected && !withPill ? pulseSelectedSurface($theme, $disabled) : transparentSurface

  return { color: $isSelected ? selectedColor : idleColor, ...surface }
}

const pulseHoverColor = ($theme, $isSelected, $disabled) => {
  if ($disabled) return $theme.colors.textPulseFaint
  return $isSelected ? $theme.colors.white : $theme.colors.primaryPulse
}

export const reducedMotion = { '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' } }

export const SDButtonGroupButtonPulseOverrides = (withPill, stretch = true) => ({
  BaseButton: {
    style: ({ $theme, $disabled, $isSelected }) => ({
      position: 'relative',
      flexDirection: 'column',
      flexGrow: stretch ? 1 : 0,
      flexShrink: 1,
      flexBasis: 'auto',
      height: '28px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '10px',
      paddingRight: '10px',
      marginLeft: 0,
      marginRight: 0,
      ...noBorder,
      ...pulseRadius('6px'),
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: $isSelected ? 600 : 500,
      whiteSpace: 'nowrap',
      transitionProperty: $disabled ? 'none' : 'background-color, box-shadow, color',
      transitionDuration: '150ms',
      transitionTimingFunction: 'ease',
      ...reducedMotion,
      ...pulseSegmentColors($theme, $isSelected, $disabled, withPill),
      ':hover': {
        color: pulseHoverColor($theme, $isSelected, $disabled),
        backgroundColor: pulseSegmentColors($theme, $isSelected, $disabled, withPill).backgroundColor,
      },
      ':disabled': pulseSegmentColors($theme, $isSelected, true, withPill),
      '::after': {
        content: 'attr(data-label)',
        height: 0,
        overflow: 'hidden',
        visibility: 'hidden',
        fontWeight: 600,
        pointerEvents: 'none',
      },
    }),
  },
})

export const SDButtonGroupOverrides = (direction, error) => {
  return {
    Root: {
      style: ({ $theme }) => {
        return {
          flexDirection: direction ?? 'initial',
          backgroundColor: $theme.colors.bgMuted,
          width: 'auto',
          marginLeft: '0.01rem',
          border: error ? `solid 2px ${$theme.colors.errorInput}` : `solid 1px ${$theme.colors.borderGroupIdle}`,
          borderTopRightRadius: $theme.borders.radius200,
          borderBottomRightRadius: $theme.borders.radius200,
          borderBottomLeftRadius: $theme.borders.radius200,
          borderTopLeftRadius: $theme.borders.radius200,
        }
      },
    },
  }
}
export const SDButtonGroupButtonOverrides = () => {
  return {
    BaseButton: {
      style: ({ $theme, $disabled, $isSelected }) => {
        const bgColour = $isSelected ? $theme.colors.white : $theme.colors.bgMuted
        const textColor = $disabled ? $theme.colors.textBtnDisabled : $isSelected ? $theme.colors.primary400 : 'inherit'
        const border = $isSelected ? `1px solid ${$theme.colors.primaryAlt}` : '1px solid transparent'
        const hoverOutline = $disabled ? 'none' : `0.125rem solid ${$theme.colors.primaryAlt}`

        return {
          flex: '1 1 auto',
          color: textColor,
          backgroundColor: bgColour,
          border,
          borderRadius: '.375rem',
          ':hover': {
            outline: hoverOutline,
            outlineOffset: '-.125rem',
          },
          ':focus': {
            outline: hoverOutline,
            outlineOffset: '-.125rem',
          },
          ':disabled': {
            backgroundColor: bgColour,
          },
          ':disabled :hover': {
            backgroundColor: bgColour,
          },
          ':disabled :focus': {
            backgroundColor: bgColour,
          },
          ':not(:last-child)': {
            marginRight: '.1875rem',
          },
        }
      },
    },
  }
}
